import { Injectable } from '@angular/core';
import {BehaviorSubject, from, map, mergeMap, Observable, tap} from "rxjs";
import {AuthConfig, L1Config} from "../model/auth-config";
import {HttpClient, HttpHeaders, HttpParams, HttpUrlEncodingCodec} from "@angular/common/http";
import {JwksValidationHandler, OAuthService} from "angular-oauth2-oidc";
import {StorageService} from "./storage.service";
import {IJWTToken, IPublicKey, IToken, UserInfo} from "../model/token";
import {MillisecondsToSecondsPipe} from "../pipes/millis-to-seconds.pipe";
import {sha256} from 'js-sha256';


@Injectable({
  providedIn: 'root'
})
export class L1L3AuthService {
  private initialised = false;
  private _isAuthenticatedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _securedHosts: string[] = [];

  private _l1Config!: L1Config;
  private _l3Config!: AuthConfig;
  public NAVIGATE_AFTER_LOGIN = 'navigate_after_login';

  constructor(private httpClient: HttpClient,
              private oAuthService: OAuthService,
              private storage: StorageService,
              private millisecondsToSeconds: MillisecondsToSecondsPipe) {
  }

  get isAuthenticatedSubject(): BehaviorSubject<boolean> {
    return this._isAuthenticatedSubject;
  }

  public get l1Config(): L1Config {
    return this._l1Config;
  }

  private set l1Config(config: L1Config) {
    this._l1Config = config;
  }

  public get l3Config(): AuthConfig {
    return this._l3Config;
  }

  private set l3Config(config: AuthConfig) {
    this._l3Config = config;
  }

  public getL1Token(): IToken {
    return this.storage.getObject(this.l1Config.accessTokenStorageKey);
  }

  private setL1Token(token: IToken) {
    this.storage.setObject(this.l1Config.accessTokenStorageKey, token);
  }

  public getL3Token(): IToken {
    return this.storage.getObject(this.l3Config.accessTokenStorageKey);
  }

  private setL3Token(token: IToken) {
    this.storage.setObject(this.l3Config.accessTokenStorageKey, token);
  }

  public getIDToken(): Promise<string> {
    return this.storage.getObject(this.l1Config?.idTokenStorageKey!);
  }

  private setIDToken(id_token: string) {
    this.storage.setObject(this.l1Config?.idTokenStorageKey!, id_token);
  }

  private set securedHosts(hosts: string[]) {
    this._securedHosts = hosts;
  }

  public get securedHosts(): string[] {
    return this._securedHosts;
  }

  public initAuth(l1Config: L1Config | null, l3Config: AuthConfig | null, securedHosts: string[]): boolean {
    if (!this.initialised) {

      if (l1Config) this.l1Config = l1Config;

      if (l3Config) this.l3Config = l3Config;

      if (securedHosts) this.securedHosts = securedHosts;

      this.initialised = true;

      this.checkTokens();
    } else {
      console.warn('Auth service already initialized!');
    }
    return this.initialised;
  }

  private checkTokens() {
    const tokenL1 = this.getL1Token();
    const tokenL3 = this.getL3Token();

    if (tokenL1 && tokenL3) {
      if (this.isJWTTokenExpired(tokenL1.refresh_token)) {
        this.logout();
      } else {
        this.isAuthenticatedSubject.next(true);
      }
    }
  }


  public requestL1Token(authCode: string): Observable<any> {
    let nonce = localStorage.getItem('nonce');

    if (!nonce) {
      nonce = sessionStorage.getItem('nonce');
    }

    const params = new HttpParams({
      fromObject: {
        scope: this.l1Config?.scope,
        grant_type: 'authorization_code',
        code: authCode,
        client_id: this.l1Config?.clientId,
        code_verifier: nonce || '',
        redirect_uri: this.l1Config?.redirectUrl
      }
    });

    return this.validateL1Token(params);
  }

  private validateL1Token(params: HttpParams): Observable<IToken> {
    return this.httpClient.post<IToken>(this.l1Config?.host + this.l1Config?.paths?.token,
      params.toString(),
      {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
      .pipe(
        mergeMap(tokenObj => {
          return this.getPublicKey().pipe(
            mergeMap(publicKey => {
              return this.verifyIdToken(tokenObj.access_token, publicKey).pipe(
                map(val => val = tokenObj),
                tap(token => {
                  this.setL1Token(token);
                  this.setIDToken(tokenObj.id_token);
                  if (this.getL3Token() && !this.checkIfTokenMailsAreEqual()) {
                    this.logout();
                  }
                })
              );
            })
          );
        })
      );
  }

  public refreshL1Token(): Observable<any> {
    const tokenObj: IToken = this.getL1Token();

    if (tokenObj) {
      const params = new HttpParams({
        fromObject: {
          grant_type: 'refresh_token',
          refresh_token: tokenObj.refresh_token,
          client_id: this.l1Config?.clientId,
          redirect_uri: this.l1Config?.redirectUrl
        }
      });
      return this.validateL1Token(params);
    } else {
      throw Error('L1 token does not exist');
    }
  }

  public requestL3Token(): Observable<any> {
    const tokenL1 = this.getL1Token();
    const tokenL1Decoded = this.decodeJWTToken(tokenL1.access_token);
    const params = new HttpParams({
      fromObject: {
        grant_type: 'password',
        username: tokenL1Decoded.payload.preferred_username,
        password: '',
        scope: this.l3Config?.scope
      },
      encoder: new HttpUrlEncodingCodec()
    });
    return this.sendL3TokenRequest(params);
  }

  public refreshL3Token(): Observable<any> {
    const tokenL3 = this.getL3Token();
    const tokenL1 = this.getL1Token();
    const tokenL1Decoded = this.decodeJWTToken(tokenL1.access_token);
    const params = new HttpParams({
      fromObject: {
        grant_type: 'refresh_token',
        username: tokenL1Decoded.payload.preferred_username,
        refresh_token: tokenL3.refresh_token
      },
      encoder: new HttpUrlEncodingCodec()
    });

    return this.sendL3TokenRequest(params);
  }

  private sendL3TokenRequest(params: HttpParams): Observable<IToken> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: 'Basic ' + btoa(`${this.l3Config?.clientId}:`)
    });

    return this.httpClient.post<IToken>(this.l3Config?.host + this.l3Config?.paths?.auth,
      params.toString(),
      {headers})
      .pipe(tap(token => {
        this.setL3Token(token);
        if (this.getL1Token() && !this.checkIfTokenMailsAreEqual()) {
          this.logout();
        } else {
          this.isAuthenticatedSubject.next(true);
        }
      }));
  }

  public async goToLogin(redirectParam?: string) {
    console.log("Go to keycloak login")
    if (redirectParam?.length && redirectParam.length > 0) {
      sessionStorage.setItem(this.NAVIGATE_AFTER_LOGIN, redirectParam);
    }
    const params = await this.getPKCEChallengeParams();
    window.location.href = this.l1Config?.host + this.l1Config?.paths?.auth + '?' + params.toString();
  }

  goToProfile() {
    window.open(this.l1Config?.host + this.l1Config?.paths?.account + '?referrer=' + this.l1Config?.clientId, "_self");
  }


  private async getPKCEChallengeParams(): Promise<HttpParams> {
    console.log("Get PKCE Challenge")
    let verifier;
    const nonceFromLS = localStorage.getItem('nonce');
    if (nonceFromLS) {
      console.log("Get PKCE Challenge - use existing")
      verifier = nonceFromLS;
    } else {
      console.log("Get PKCE Challenge - create new one")
      verifier = await this.oAuthService.createAndSaveNonce();
    }

    const challengeRaw = await this.calcHashS256(verifier);
    const challenge = this.base64UrlEncode(challengeRaw);

    return new HttpParams({
      fromObject: {
        'scope': this.l1Config?.scope,
        'redirect_uri': this.l1Config?.redirectUrl,
        'client_id': this.l1Config?.clientId,
        'response_type': 'code',
        'code_challenge': challenge,
        'code_challenge_method': 'S256'
      }
    });
  }

  public logout() {
    this.logoutFromAuthServer().subscribe({
      error: err => {
        //no handling required
      }
    });
    this.goToLogout();
  }

  public logoutFromAuthServer(): Observable<any> {
    this.isAuthenticatedSubject.next(false)
    return this.httpClient.post<any>(this.l3Config?.host + this.l3Config?.paths?.logout, {});
  }

  public async goToLogout() {
    const id_token = await this.getIDToken();
    this.isAuthenticatedSubject.next(false)
    this.clearTokens();
    let params;
    if(id_token) {
      params = new HttpParams({
        fromObject: {
          'post_logout_redirect_uri': this.l1Config?.logoutRedirectUrl,
          'id_token_hint': id_token
        }
      });
    }else{
      params = new HttpParams({
        fromObject: {
          'post_logout_redirect_uri': this.l1Config?.logoutRedirectUrl,
          'client_id':this.l1Config?.clientId
        }
      });
    }
    window.location.href = this.l1Config?.host + this.l1Config?.paths?.logout + '?' + params.toString();
  }

  public isJWTTokenExpired(token: string): boolean {
    try {
      const tokenDecoded = this.decodeJWTToken(token);
      const currentUTCTimestamp = Math.round(this.millisecondsToSeconds.transform(new Date().getTime()));
      return currentUTCTimestamp >= tokenDecoded.payload.exp;
    } catch (e) {
      return true;
    }
  }

  public getPublicKey(): Observable<IPublicKey> {
    return this.httpClient.get<IPublicKey>(this.l1Config?.host + this.l1Config.paths?.publicKey);
  }

  public clearTokens() {
    console.log("Tokens removed...")
    this.storage.removeObjects([this.l1Config?.accessTokenStorageKey, this.l3Config?.accessTokenStorageKey, this.l1Config?.idTokenStorageKey!, 'nonce']);
  }

  public getUserInfo(): UserInfo {
    const tokenL1 = this.getL1Token();
    const tokenL3 = this.getL3Token();
    let userInfo = new UserInfo();

    try {

      if (tokenL1) {
        const tokenL1Decoded: IJWTToken = this.decodeJWTToken(tokenL1.access_token);
        userInfo.display_name = `${tokenL1Decoded.payload.given_name} ${tokenL1Decoded.payload.family_name}`;
        userInfo.userId = tokenL1Decoded.payload.sub;
        userInfo.email = tokenL1Decoded.payload.email;
        userInfo.phone_number = tokenL1Decoded.payload.phone_number;
      }

      if (tokenL3) {
        const tokenL3Decoded: IJWTToken = this.decodeJWTToken(tokenL3.access_token);
        userInfo.authorities = tokenL3Decoded.payload.authorities;
        userInfo.domainId = tokenL3Decoded.payload.did;
      }
    } catch (e) {
      // No handling required.
    }

    return userInfo;
  }


  private verifyIdToken(token: string, publicKey: IPublicKey): Observable<any> {
    const idTokenDecoded = this.decodeJWTToken(token);
    const handler = new JwksValidationHandler();
    return from(handler.validateSignature({
      idToken: token,
      accessToken: '',
      idTokenHeader: idTokenDecoded.header,
      idTokenClaims: {},
      loadKeys: () => {
        return new Promise(resolve => resolve({}))
      },
      jwks: publicKey
    }));
  }

  private decodeJWTToken(token: string): IJWTToken {
    const parts = token.split('.');
    let header;
    let payload;

    if (parts.length !== 3) {
      throw Error('Cannot decode a malformed JWT');
    }

    try {
      header = JSON.parse(this.urlBase64Decode(parts[0]));
      payload = JSON.parse(this.urlBase64Decode(parts[1]));
    } catch (e: any) {
      throw Error(e);
    }

    return {
      header,
      payload,
      encoded: {
        header: parts[0],
        payload: parts[1],
        signature: parts[2]
      }
    };
  }

  checkPermissions(permissions: string[]): boolean {
    let accessAllowed: boolean = false;
    permissions?.forEach(permission => {
      if (this.getUserInfo()?.authorities?.includes(permission)) {
        accessAllowed = true;
        return;
      }
    });
    return accessAllowed;
  }

  clearL3Token(): void {
    this.storage.removeObjects([this.l3Config.accessTokenStorageKey]);
  }


  /**
   * Helper function from angular-oauth2-oidc lib.
   * @param verifier
   */
  private calcHashS256(verifier: string) {
    const hashArray = sha256.array(verifier);
    return this.toHashString2(hashArray);
  }

  /**
   * Helper function from angular-oauth2-oidc lib.
   * @param byteArray
   */
  private toHashString2(byteArray: number[]) {
    let result = '';
    for (let e of byteArray) {
      result += String.fromCharCode(e);
    }
    return result;
  }

  /**
   * Helper function from angular-oauth2-oidc lib.
   * @param str
   */
  private base64UrlEncode(str: string): string {
    const base64 = btoa(str);
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  //TODO =====> Find another solution for the decoding! "atob" is buggy and this is just a fix from nebular (recommended to go away from it)
  /**
   * Helper function for UTF-8 decoding of a b64 encoded string.
   * @param str
   */
  private b64DecodeUnicode(str: string): string {
    // Going backwards: from bytestream, to percent-encoding, to original string.
    return decodeURIComponent(Array.prototype.map.call(this.b64decode(str), function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }

  private urlBase64Decode(str: string): string {
    let output = str.replace(/-/g, '+').replace(/_/g, '/');
    switch (output.length % 4) {
      case 0: {
        break;
      }
      case 2: {
        output += '==';
        break;
      }
      case 3: {
        output += '=';
        break;
      }
      default: {
        throw new Error('Illegal base64url string!');
      }
    }
    return this.b64DecodeUnicode(output);
  }

  private b64decode(str: string) {
    var chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    var output = '';

    str = String(str).replace(/=+$/, '');

    if (str.length % 4 === 1) {
      console.error("'atob' failed: The string to be decoded is not correctly encoded.");
    }

    for (
      // initialize result and counters
      var bc = 0, bs, buffer, idx = 0;
      // get next character
      buffer = str.charAt(idx++);
      // character found in table? initialize bit storage and add its ascii value;
      ~buffer && (bs = bc % 4 ? (bs || 0) * 64 + buffer : buffer,
        // and if not first of each 4 characters,
        // convert the first 8 bits to one ascii character
      bc++ % 4) ? output += String.fromCharCode(255 & bs >> (-2 * bc & 6)) : 0
    ) {
      // try to find character in table (0-63, not found => -1)
      buffer = chars.indexOf(buffer);
    }
    return output;
  }

  checkIfTokenMailsAreEqual(): boolean {
    console.log('Check if token mails are equal')
    const tokenL1 = this.getL1Token();
    const tokenL3 = this.getL3Token();

    let email1 = '';
    let email3 = '';
    try {
      if (tokenL1) {
        const tokenL1Decoded: IJWTToken = this.decodeJWTToken(tokenL1.access_token);
        email1 = tokenL1Decoded.payload.email;
      }

      if (tokenL3) {
        const tokenL3Decoded: IJWTToken = this.decodeJWTToken(tokenL3.access_token);
        email3 = tokenL3Decoded.payload.email;
      }
    } catch (e) {
      // No handling required.
    }

    console.log("Mail1: " + email1);
    console.log("Mail3: " + email3);

    return email1?.length > 0 && email3?.length > 0 && email1 === email3;
  }
}
